class BuildYourBandana extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      inputVariant: '[data-opt-choice]',
      letteringContainer: '[data-lettering-container]',
      threadContainer: '[data-thread-container]',
      nameContainer: '[data-name-container]',
      validationError: '[data-embroidery-error]',
      neckSize: '[data-neck-size]',
      neckUnit: '[data-neck-unit]',
      neckError: '[data-neck-error]',
    };
  }

  connectedCallback() {
    this.inputs = this.querySelectorAll(this.selectors.inputVariant);
    this.letteringContainer = this.querySelector(this.selectors.letteringContainer);
    this.threadContainer = this.querySelector(this.selectors.threadContainer);
    this.nameContainer = this.querySelector(this.selectors.nameContainer);
    this.validationError = this.querySelector(this.selectors.validationError);
    this.neckSizeInput = this.querySelector(this.selectors.neckSize);
    this.neckUnitInput = this.querySelector(this.selectors.neckUnit);
    this.neckError = this.querySelector(this.selectors.neckError);

    if (this.inputs.length) {
      this.inputs.forEach((input) => {
        input.addEventListener('change', () => this.toggleEmbroidery(input));
      });
    }

    if (this.neckSizeInput) {
      this.neckSizeInput.addEventListener('input', () => this.validateNeckMeasurement(false));
      this.neckSizeInput.addEventListener('blur', () => this.validateNeckMeasurement(false));
    }
    if (this.neckUnitInput) {
      this.neckUnitInput.addEventListener('change', () => this.validateNeckMeasurement(false));
    }

    // Keep the personalisation controls usable at all times. They are hidden
    // until embroidery is chosen, but are never disabled, so they cannot get
    // stuck greyed-out after product/variant updates.
    this.setEmbroideryFieldsEnabled(true);
    const embroideryYes = this.querySelector('#byb-embroidery-yes');
    if (embroideryYes?.checked) {
      this.showSection(this.letteringContainer);
      this.showSection(this.threadContainer);
      this.showSection(this.nameContainer);
    } else {
      this.hideSection(this.letteringContainer);
      this.hideSection(this.threadContainer);
      this.hideSection(this.nameContainer);
    }

    this.productForm = this.getProductForm();
    if (this.productForm) {
      this.productForm.addEventListener('submit', (event) => this.handleProductSubmit(event), true);
    }
  }

  getProductForm() {
    const directForm = this.closest('form[action*="/cart/add"]');
    if (directForm?.querySelector('[name="add"]')) return directForm;

    const productInfo = this.closest('product-info');
    const productInfoForm = Array.from(productInfo?.querySelectorAll('form[action*="/cart/add"]') || [])
      .find((form) => form.querySelector('[name="add"]'));
    if (productInfoForm) return productInfoForm;

    const section = this.closest('.shopify-section');
    const sectionForm = Array.from(section?.querySelectorAll('form[action*="/cart/add"]') || [])
      .find((form) => form.querySelector('[name="add"]'));
    if (sectionForm) return sectionForm;

    return Array.from(document.querySelectorAll('form[action*="/cart/add"]'))
      .find((form) => form.querySelector('[name="add"]')) || null;
  }

  setProperty(form, key, value) {
    const name = `properties[${key}]`;
    const existing = form.querySelector(`input[name="${name}"]`);

    if (!value) {
      if (existing && existing.type === 'hidden') existing.remove();
      return;
    }

    if (existing && existing.type === 'hidden') {
      existing.value = value;
      return;
    }

    const hidden = document.createElement('input');
    hidden.type = 'hidden';
    hidden.name = name;
    hidden.value = value;
    form.appendChild(hidden);
  }

  getCheckedValue(container) {
    if (!container) return '';
    const checked = container.querySelector('input:checked');
    return checked?.value?.trim() || '';
  }

  setEmbroideryFieldsEnabled() {
    [this.letteringContainer, this.threadContainer, this.nameContainer].forEach((container) => {
      if (!container) return;
      container.querySelectorAll('input, select, textarea').forEach((field) => {
        field.disabled = false;
      });
    });
  }

  clearValidationError() {
    if (!this.validationError) return;
    this.validationError.hidden = true;
    this.validationError.textContent = '';
  }

  showValidationError(message, target) {
    if (this.validationError) {
      this.validationError.textContent = message;
      this.validationError.hidden = false;
    }

    if (target) {
      target.focus({ preventScroll: true });
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  validateNeckMeasurement(focusOnError = false) {
    if (!this.neckSizeInput) return true;

    const raw = this.neckSizeInput.value.trim();
    if (raw === '') {
      if (this.neckError) {
        this.neckError.hidden = true;
        this.neckError.textContent = '';
      }
      this.neckSizeInput.removeAttribute('aria-invalid');
      return true;
    }

    const value = Number(raw);
    if (!Number.isFinite(value) || value <= 0) {
      if (this.neckError) {
        this.neckError.textContent = 'Please enter a valid neck measurement using numbers only.';
        this.neckError.hidden = false;
      }
      this.neckSizeInput.setAttribute('aria-invalid', 'true');
      if (focusOnError) {
        this.neckSizeInput.focus({ preventScroll: true });
        this.neckSizeInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return false;
    }

    if (this.dataset.measurementInRange === 'false') {
      if (this.neckError) {
        this.neckError.textContent = 'No listed size fits this measurement. Please contact me before ordering.';
        this.neckError.hidden = false;
      }
      this.neckSizeInput.setAttribute('aria-invalid', 'true');
      if (focusOnError) {
        this.neckSizeInput.focus({ preventScroll: true });
        this.neckSizeInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return false;
    }

    if (this.neckError) {
      this.neckError.hidden = true;
      this.neckError.textContent = '';
    }
    this.neckSizeInput.removeAttribute('aria-invalid');
    return true;
  }

  validateEmbroidery() {
    const embroideryYes = this.querySelector('#byb-embroidery-yes');
    if (!embroideryYes?.checked) {
      this.clearValidationError();
      return true;
    }

    const dogNameInput = this.querySelector('#byb-dog-name');
    const dogName = dogNameInput?.value?.trim() || '';
    const letteringStyle = this.getCheckedValue(this.letteringContainer);
    const threadColour = this.getCheckedValue(this.threadContainer);

    if (!dogName) {
      this.showValidationError('Please enter the dog’s name or text you want embroidered.', dogNameInput);
      return false;
    }

    if (!letteringStyle) {
      const firstLettering = this.letteringContainer?.querySelector('input');
      this.showValidationError('Please choose a lettering style before adding embroidery.', firstLettering);
      return false;
    }

    if (!threadColour) {
      const firstThread = this.threadContainer?.querySelector('input');
      this.showValidationError('Please choose a thread colour before adding embroidery.', firstThread);
      return false;
    }

    this.clearValidationError();
    return true;
  }

  handleProductSubmit(event) {
    if (!this.validateNeckMeasurement(true)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (!this.validateEmbroidery()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    this.syncNeckSizeProperty(this.productForm);
    this.syncEmbroideryProperties(this.productForm);
  }

  syncNeckSizeProperty(form) {
    if (!this.neckSizeInput) return;

    this.setProperty(form, 'Neck size', '');
    this.setProperty(form, 'Neck measurement', '');
    this.setProperty(form, 'Collar length', '');

    const raw = this.neckSizeInput.value?.trim() || '';
    if (!raw) return;

    const value = Number(raw);
    if (!Number.isFinite(value) || value <= 0) return;

    const unit = this.neckUnitInput?.value === 'in' ? 'inches' : 'cm';
    const cleanedValue = Number.isInteger(value) ? String(value) : String(Math.round(value * 10) / 10);
    const propertyName = this.dataset.bandanaStyle === 'over-collar' ? 'Collar length' : 'Neck measurement';
    this.setProperty(form, propertyName, `${cleanedValue} ${unit}`);
  }

  syncEmbroideryProperties(form) {
    const embroideryYes = this.querySelector('#byb-embroidery-yes');
    const hasEmbroidery = Boolean(embroideryYes?.checked);

    if (!hasEmbroidery) {
      this.setProperty(form, 'Embroidery', '');
      this.setProperty(form, 'Lettering Style', '');
      this.setProperty(form, 'Thread Color', '');
      this.setProperty(form, 'Dog name', '');
      return;
    }

    const dogNameInput = this.querySelector('#byb-dog-name');
    const dogName = dogNameInput?.value?.trim() || '';
    const letteringStyle = this.getCheckedValue(this.letteringContainer);
    const threadColour = this.getCheckedValue(this.threadContainer);

    this.setProperty(form, 'Embroidery', 'Yes');
    this.setProperty(form, 'Lettering Style', letteringStyle);
    this.setProperty(form, 'Thread Color', threadColour);
    this.setProperty(form, 'Dog name', dogName);
  }

  toggleEmbroidery(input) {
    if (input.id === 'byb-embroidery-yes' && input.checked) {
      this.setEmbroideryFieldsEnabled(true);
      this.clearValidationError();
      this.showSection(this.letteringContainer);
      this.showSection(this.threadContainer);
      this.showSection(this.nameContainer);
    } else if (input.id === 'byb-embroidery-no' && input.checked) {
      this.clearValidationError();
      this.hideSection(this.letteringContainer);
      this.hideSection(this.threadContainer);
      this.hideSection(this.nameContainer);
    }
  }

  showSection(section) {
    if (!section) return;
    section.classList.add('active');
    section.style.setProperty('--max-height', section.scrollHeight + 'px');
  }

  hideSection(section) {
    if (!section) return;
    section.classList.remove('active');
    section.style.setProperty('--max-height', '0px');
  }
}

if (!customElements.get('build-your-bandana')) {
  customElements.define('build-your-bandana', BuildYourBandana);
}
